import React from "react";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";

import { Action, Category } from "constants/events";
import { Deal } from "custom-types/Deals";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import useGetDealKindLabel from "hooks/useGetDealKindLabel";
import { getUserId } from "redux/selectors/user";
import { dealCardUpdateMedRec } from "utils/dealCardUpdateMedRec";
import { getDispensaryUrl } from "utils/dispensaryUrls";
import { getRoundedDistance } from "utils/distance";

import DealCard from "components/botanic//DealCard";
import Carousel from "components/botanic/Carousel";

import { ShallowRenderingWrapper } from "../ShallowRenderingWrapper";

type Props = {
  className?: string;
  disableShallowRendering?: boolean;
  deals?: Deal[];
  cardsPerSlide?: number;
};

const DealsCarousel: React.FC<Props> = ({
  cardsPerSlide = 3,
  className,
  disableShallowRendering = false,
  deals = [],
}) => {
  const countryCode = useDomainCountryCode();
  const userId = useSelector(getUserId);
  const dispatch = useDispatch();

  const { publishEvent } = useEventTracker();
  const { getDealKindLabel } = useGetDealKindLabel();

  if (!deals.length) {
    return null;
  }

  const cardClick = async (deal: Deal) => {
    const {
      dispensary: { id: dispensaryId },
    } = deal;

    publishEvent({
      action: Action.click,
      category: Category.dealCards,
      dealId: deal.id,
      dispensaryId,
      label: getDealKindLabel(deal),
      userId,
    });

    // Med/rec deals will have medical and/or recreational properties, check if we need to update the user preference
    if (deal.isMedical || deal.isRecreational) {
      await dealCardUpdateMedRec(deal, dispatch);
    }
  };

  const cardImpression = (deal: Deal) => {
    const {
      dispensary: { id: dispensaryId, distanceMi },
    } = deal;

    publishEvent({
      action: Action.impression,
      category: Category.dealCards,
      dealId: deal.id,
      dispensaryDistance: getRoundedDistance(distanceMi, countryCode),
      dispensaryId,
      label: getDealKindLabel(deal),
      userId,
    });
  };

  return (
    <div className={classnames(className)}>
      <Carousel title="Deals on nearby weed" cardsPerSlide={cardsPerSlide}>
        {deals.map((deal) => {
          const {
            title,
            dispensary: { slug },
          } = deal;

          const isDealCBD = !deal.isMedical && !deal.isRecreational;
          const dealType =
            deal.isMedical && !deal.isRecreational ? "Med" : "Rec";

          const dealUrl = getDispensaryUrl({
            countryCode,
            query: {
              deal_title: [title],
              ...(!isDealCBD && { menu_type: dealType }),
            },
            slug,
            tab: "menu",
          });

          return (
            <ShallowRenderingWrapper
              enabled={disableShallowRendering}
              href={dealUrl}
              key={deal.id}
            >
              <DealCard
                deal={{ ...deal, dealUrl }}
                showFulfillment={true}
                countryCode={countryCode}
                className="rounded bg-white elevation-low h-full overflow-hidden"
                onClick={() => cardClick(deal)}
                onLoad={() => cardImpression(deal)}
              />
            </ShallowRenderingWrapper>
          );
        })}
      </Carousel>
    </div>
  );
};

export default DealsCarousel;
